import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { colors } from './Theme/WebTheme';

const AdminPermissionDialog = ({ open, onClose, onSave, editingUser = null }) => {
  const { t } = useTranslation();
  const addonsdata = useSelector(state => state.addonsdata);
  
  // Check if sub-admin controls addon is enabled
  const isSubAdminControlsEnabled = addonsdata.addons && addonsdata.addons.sub_admin_controls;
  
  const [permissionType, setPermissionType] = useState('all');
  const [specificPermissions, setSpecificPermissions] = useState({
    dashboard: false,
    bookings: false,
    queuedOrders: false,
    addbookings: false,
    users: false,
    pricing: false,
    bank_account: false,
    subscriptions: false,
    loyalty_points: false,
    cars: false,
    addtowallet: false,
    yappy_wallet_topups: false,
    withdraws: false,
    promos: false,
    notifications: false,
    cancelreasons: false,
    allreports: false,
    complain: false,
    support: false,
    powertranz_transactions: false,
    documentmanagement: false,
    feedback_review: false,
    settings: false,
    userwallet: false
  });

  // Available pages for permission control
  const availablePages = [
    { key: 'dashboard', label: t('dashboard_text') || 'Dashboard / Panel', url: '/dashboard' },
    { key: 'bookings', label: t('booking_history') || 'Reservations', url: '/bookings' },
    { key: 'queuedOrders', label: t('admin_queue') || 'Queued Orders', url: '/queued-orders' },
    { key: 'addbookings', label: t('addbookinglable') || 'Add Reservations', url: '/addbookings' },
    { key: 'users', label: t('users') || 'Users', url: '/users' },
    { key: 'pricing', label: t('pricing') || 'Prices', url: '/pricing' },
    { key: 'bank_account', label: t('bank_account') || 'Bank Account', url: '/bank-account' },
    { key: 'subscriptions', label: t('subscription') || 'Subscriptions', url: '/subscriptions' },
    { key: 'loyalty_points', label: t('loyalty_points') || 'Loyalty Points', url: '/loyalty-points' },
    { key: 'cars', label: t('cars') || 'cars', url: '/cars' },
    { key: 'addtowallet', label: t('add_to_wallet') || 'Add To Wallet', url: '/addtowallet' },
    { key: 'yappy_wallet_topups', label: t('yappy_wallet_topups') || 'Yappy Wallet Topups', url: '/yappy-wallet-topups' },
    { key: 'withdraws', label: t('withdraws'), url: '/withdraws' },
    { key: 'promos', label: t('promo') || 'Promotions', url: '/promos' },
    { key: 'notifications', label: t('push_notification_title'), url: '/notifications' },
    { key: 'cancelreasons', label: t('cancellation_reasons'), url: '/cancelreasons' },
    { key: 'allreports', label: t('report'), url: '/allreports' },
    { key: 'complain', label: t('complain'), url: '/complain' },
    { key: 'support', label: t('support'), url: '/support' },
    { key: 'powertranz_transactions', label: t('powertranz_transactions') || 'PowerTranz Transactions', url: '/powertranz-transactions' },
    { key: 'documentmanagement', label: t('document_management') || 'Document Management', url: '/documentmanagement' },
    { key: 'feedback_review', label: t('feedback_review') || 'Feedback Review', url: '/feedback-review' },
    { key: 'settings', label: t('settings') || 'Settings', url: '/settings' },
    { key: 'userwallet', label: t('my_wallet_tile') || 'User Wallet', url: '/userwallet' }
  ];

  // Initialize permissions when editing user
  useEffect(() => {
    if (editingUser && editingUser.subAdminPermissions) {
      if (editingUser.subAdminPermissions.type === 'all') {
        setPermissionType('all');
      } else {
        setPermissionType('specific');
        setSpecificPermissions(editingUser.subAdminPermissions.pages || {});
      }
    } else {
      // Default to all permissions for new users
      setPermissionType('all');
      setSpecificPermissions({
        dashboard: false,
        bookings: false,
        queuedOrders: false,
        addbookings: false,
        users: false,
        pricing: false,
        bank_account: false,
        subscriptions: false,
        loyalty_points: false,
        cars: false,
        addtowallet: false,
        yappy_wallet_topups: false,
        withdraws: false,
        promos: false,
        notifications: false,
        cancelreasons: false,
        allreports: false,
        complain: false,
        support: false,
        powertranz_transactions: false,
        documentmanagement: false,
        feedback_review: false,
        settings: false,
        userwallet: false
      });
    }
  }, [editingUser]);

  const handlePermissionTypeChange = (event) => {
    setPermissionType(event.target.value);
  };

  const handleSpecificPermissionChange = (pageKey) => {
    setSpecificPermissions(prev => ({
      ...prev,
      [pageKey]: !prev[pageKey]
    }));
  };

  const handleSave = () => {
    const permissions = {
      type: permissionType,
      pages: permissionType === 'specific' ? specificPermissions : null
    };
    
    onSave(permissions);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  // If sub-admin controls addon is not enabled, don't show the dialog
  if (!isSubAdminControlsEnabled) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '8px',
          boxShadow: '4px 4px 6px #9E9E9E'
        }
      }}
    >
      <DialogTitle style={{ backgroundColor: colors.MAIN_COLOR, color: colors.BLACK }}>
        {t('page_access_controls') || 'Page Access Controls'}
      </DialogTitle>
      
      <DialogContent style={{ padding: '20px' }}>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {t('sub_admin_controls_description') || 'Configure which pages this administrator can access.'}
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" style={{ marginBottom: '10px', fontWeight: 'bold' }}>
            {t('permission_type') || 'Permission Type'}
          </FormLabel>
          
          <RadioGroup
            value={permissionType}
            onChange={handlePermissionTypeChange}
            style={{ marginBottom: '20px' }}
          >
            <FormControlLabel
              value="all"
              control={<Radio color="primary" />}
              label={t('allow_all_controls') || 'Allow All Controls'}
            />
            <FormControlLabel
              value="specific"
              control={<Radio color="primary" />}
              label={t('select_specific_pages') || 'Select Specific Pages'}
            />
          </RadioGroup>
        </FormControl>

        {permissionType === 'specific' && (
          <>
            <Divider style={{ margin: '20px 0' }} />
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" style={{ marginBottom: '15px', fontWeight: 'bold' }}>
                {t('available_pages') || 'Available Pages'}
              </FormLabel>
              
              <FormGroup>
                {availablePages.map((page) => (
                  <FormControlLabel
                    key={page.key}
                    control={
                      <Checkbox
                        checked={specificPermissions[page.key] || false}
                        onChange={() => handleSpecificPermissionChange(page.key)}
                        color="primary"
                      />
                    }
                    label={page.label}
                    style={{ marginBottom: '8px' }}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      
      <DialogActions style={{ padding: '20px', backgroundColor: '#f5f5f5' }}>
        <Button onClick={handleClose} color="secondary">
          {t('cancel') || 'Cancel'}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          style={{ 
            backgroundColor: colors.MAIN_COLOR, 
            color: colors.BLACK,
            marginLeft: '10px'
          }}
        >
          {t('save') || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminPermissionDialog; 